/**
 * What My Apps lists, as a pure rule both clients import.
 *
 * My Apps is the signed-in person's own apps that finished building and have a live page — the
 * sites they made and can hand out, each one a tap from its editor. It used to be the six bundled
 * demos followed by whatever the catalogue said this person had published or JOINED: other
 * people's apps, a stranger's demo with a Clone button, and no row that led to the editor of the
 * app it named. The phone and the browser built that list two different ways, too.
 *
 * Rows come first from the person's own PROJECTS (each client's project store already reads only
 * the signed-in owner's): those open the editor. Then from the server, for the one case a project
 * cannot cover — see `liveElsewhereRows`.
 *
 * Imports `siteAddress` and `websiteSheet`, both pure, and `isWebDemo` from `webDemos`, a
 * constant-list lookup; so the web builder can share it (web/src/__tests__/boundary.test.ts lists
 * it with them).
 */
import { displayAddress, siteLink } from '@/webapp/siteAddress';
import { siteStatusOf, SITE_STATUS_LABEL, type SiteListing, type SiteStatus } from '@/utils/websiteSheet';
import { isWebDemo } from '@/demos/webDemos';

/** The fields of a project's record that decide whether it is live. */
export interface LiveAppRecord {
  runtime?: string;
  webUrl?: string;
  hostedAppId?: string;
  unfinished?: unknown;
}

/** The part of a project My Apps reads. Both clients' `Project` satisfies it. */
export interface LiveAppProject {
  id: string;
  name: string;
  definition?: LiveAppRecord | null;
}

/** What the server said about each of this person's pages, keyed by catalogue id. */
export type SiteStatuses = ReadonlyMap<string, SiteStatus>;

export interface LiveAppRow<P extends LiveAppProject> {
  project: P;
  /** The page, to open. */
  url: string;
  /** The page as a person reads it — no scheme — as the Build row and the editor show it. */
  address: string;
  /**
   * The one word worth showing beside a row, or null. Every row here is up, so "Live" on each of
   * them would say nothing; a page behind a password is the state an owner should see at a glance.
   */
  badge: string | null;
}

/**
 * The server's listings of this person's apps (`/me/apps`), as Live / Password / Offline by
 * catalogue id — read by `siteStatusOf`, the same rule the editor's status chip uses, so the two
 * cannot disagree about one page.
 */
export function siteStatusesFrom(
  listings: readonly (SiteListing & { appId?: string })[] | null | undefined,
): Map<string, SiteStatus> {
  const out = new Map<string, SiteStatus>();
  for (const listing of listings ?? []) {
    if (listing?.appId) out.set(listing.appId, siteStatusOf(listing));
  }
  return out;
}

/**
 * This project as a My Apps row, or null when it is not a finished, live app.
 *
 * - Finished, with an address: `siteLink`, the rule every "open the website" control uses. It
 *   refuses an unfinished build even when it carries an older address — a rebuild that failed
 *   keeps the address of the app before it, which is not the app the row is waiting to be.
 * - A catalogue id as well: an address and an id is what `finalizeBuild` treats as "the build
 *   hosted this page". An address with no id was never registered, so nothing can manage it or
 *   ask whether it is up.
 * - Not offline, WHERE THE SERVER HAS SAID SO. A page it has not answered about is kept. The
 *   list is a capped page of enrolments and a lookup that failed returns nothing: both are facts
 *   about the moment, not about this page, and hiding a live site from its owner because the
 *   network blinked is the wrong way round for a list of their own work. Privacy is not decided
 *   here — the editor's chip asks the server itself before it says anything is public.
 */
export function liveAppRow<P extends LiveAppProject>(project: P, statuses?: SiteStatuses): LiveAppRow<P> | null {
  const record = project.definition;
  const site = siteLink(record);
  if (!site || !record?.hostedAppId) return null;
  const status = statuses?.get(record.hostedAppId);
  if (status === 'offline') return null;
  return {
    project,
    url: site.url,
    address: site.address,
    badge: status === 'password' ? SITE_STATUS_LABEL.password : null,
  };
}

/** The rows My Apps shows, in the order the projects were given (newest first, as both stores read them). */
export function liveAppRows<P extends LiveAppProject>(projects: readonly P[], statuses?: SiteStatuses): LiveAppRow<P>[] {
  const rows: LiveAppRow<P>[] = [];
  for (const project of projects) {
    const row = liveAppRow(project, statuses);
    if (row) rows.push(row);
  }
  return rows;
}

/** The part of the server's listing (`/me/apps`) the other-device rows read. */
export interface OwnListing extends SiteListing {
  appId?: string;
  name?: string;
  creatorId?: string;
  remixOnly?: boolean;
  /** Sent by the server only for a page it is serving. */
  siteUrl?: string;
}

/** One of the person's live apps that has no project on this device. */
export interface ElsewhereRow {
  /** Catalogue id. */
  appId: string;
  name: string;
  url: string;
  address: string;
  badge: string | null;
}

/**
 * The person's live apps that were made on ANOTHER device, as links to their pages.
 *
 * A project lives on the device that made it — SQLite on the phone, IndexedDB in a browser — and
 * there is no /me/projects yet. So an app built on the phone has no project in the web builder,
 * and a list built from projects alone would tell its owner, signed in on the web, that nothing
 * of theirs is live. The server knows what this person published; what has no project here is
 * listed too, opening the page, because there is no editor for it on this device.
 *
 * With no local record to fall back on, the server's word is the only word, so each must be:
 * - published BY this person (`creatorId`) — `/me/apps` also returns apps they only joined. No
 *   owner known is nobody's list, never everybody's;
 * - served, by the shared rule, and given an address (the server sends `siteUrl` only for a page
 *   that is up) — an absent switch is not a live page;
 * - not a bundled demo or a remix-only template, which are nobody's app to manage;
 * - not the catalogue id of ANY project here, finished or not. The project decides for its own
 *   app: a rebuild that failed is "not finished" on this device even while the old page is up.
 */
export function liveElsewhereRows(
  listings: readonly OwnListing[] | null | undefined,
  projects: readonly LiveAppProject[],
  ownerId: string | null | undefined,
): ElsewhereRow[] {
  if (!ownerId) return [];
  const here = new Set(projects.map((p) => p.definition?.hostedAppId).filter((id): id is string => !!id));
  const seen = new Set<string>();
  const rows: ElsewhereRow[] = [];
  for (const listing of listings ?? []) {
    const { appId, siteUrl } = listing ?? {};
    if (!appId || !siteUrl || seen.has(appId) || here.has(appId)) continue;
    if (listing.creatorId !== ownerId || listing.remixOnly || isWebDemo(appId)) continue;
    const status = siteStatusOf(listing);
    const address = displayAddress(siteUrl);
    if (status === 'offline' || !address) continue;
    seen.add(appId);
    rows.push({
      appId,
      name: listing.name?.trim() || address,
      url: siteUrl,
      address,
      badge: status === 'password' ? SITE_STATUS_LABEL.password : null,
    });
  }
  return rows;
}

/**
 * A row's name for a screen reader: the app, its address, and its badge when it has one — what a
 * sighted person reads on the card, in the same order.
 *
 * A control's label REPLACES the text inside it, so a phone row labelled with the name alone hid
 * the address and "Password" from VoiceOver, and a page behind a password is the one state an
 * owner should hear without opening it. The web row uses the same words.
 */
export function liveRowLabel(row: { name: string; address: string; badge: string | null }): string {
  return [row.name, row.address, row.badge].filter((part) => !!part && part.trim()).join(', ');
}

/**
 * The words My Apps says, on both clients.
 *
 * Here rather than in theme/strings because the empty state is part of what this list IS: it
 * used to say "Find one in Discover and tap Get", which described the old list, on a screen that
 * no longer has a Discover to find one in. `create` is the Build tab's word for the same button.
 */
export const MY_APPS_COPY = {
  loading: 'Loading your apps…',
  errorTitle: 'Couldn’t load your apps',
  errorBody: 'Check your connection and try again.',
  emptyTitle: 'Nothing live yet',
  emptyBody: 'Build your first app. Once it’s live, it shows up here.',
  create: 'Create an app',
  /** Under a row from `liveElsewhereRows`: why it opens the page rather than the editor. */
  elsewhere: 'Made on another device. Edit it there.',
} as const;
